import { create } from 'zustand'
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '../services/firebase'
import { useAuthStore } from './authStore'
import toast from 'react-hot-toast'

export const useWalletStore = create((set, get) => ({
  balance: null,
  transactions: [],
  loading: false,
  error: null,

  // Fetch user wallet balance
  fetchBalance: async () => {
    const user = useAuthStore.getState().user
    if (!user) return

    try {
      set({ loading: true })
      const userDoc = await getDoc(doc(db, 'users', user.uid))
      
      if (userDoc.exists()) {
        set({ balance: userDoc.data().wallet, loading: false })
      }
    } catch (error) {
      console.error('Error fetching balance:', error)
      set({ error: error.message, loading: false })
    }
  },

  // Fetch user transactions
  fetchTransactions: async (limitCount = 50) => {
    const user = useAuthStore.getState().user
    if (!user) return

    try {
      set({ loading: true })
      const q = query(
        collection(db, 'transactions'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      )
      
      const snapshot = await getDocs(q)
      const transactions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))

      set({ transactions, loading: false })
    } catch (error) {
      console.error('Error fetching transactions:', error)
      set({ error: error.message, loading: false })
    }
  },

  // Add deposit (creates payment proof for manual validation)
  requestDeposit: async (amount, method, proofUrl = null) => {
    const user = useAuthStore.getState().user
    if (!user) return

    try {
      set({ loading: true })

      // Create payment proof record
      await addDoc(collection(db, 'paymentProofs'), {
        userId: user.uid,
        amount,
        method, // 'mercadopago', 'clabe', 'oxxo'
        proofUrl,
        status: 'pending',
        type: 'deposit',
        createdAt: serverTimestamp(),
      })

      // Persistir movimiento en historial de transacciones
      await addDoc(collection(db, 'transactions'), {
        userId: user.uid,
        type: 'deposit',
        method,
        amount,
        status: 'pending',
        description: method === 'clabe' ? 'Depósito por transferencia CLABE' : 'Depósito',
        createdAt: serverTimestamp(),
      })

      toast.success('Solicitud de depósito enviada. En espera de validación.')
      set({ loading: false })
      return { success: true }
    } catch (error) {
      console.error('Error requesting deposit:', error)
      toast.error('Error al solicitar depósito')
      set({ error: error.message, loading: false })
      return { success: false, error: error.message }
    }
  },

  // Request withdrawal
  requestWithdrawal: async (amount, clabeDestination, beneficiaryName) => {
    const user = useAuthStore.getState().user
    const userData = useAuthStore.getState().userData
    if (!user || !userData) return { success: false, error: 'Not authenticated' }

    try {
      set({ loading: true })

      // Check if user has enough winnings to withdraw
      const { balance } = get()
      if (!balance || balance.winnings < amount) {
        toast.error('Saldo insuficiente para retirar')
        set({ loading: false })
        return { success: false, error: 'Insufficient winnings' }
      }

      // Check KYC verification
      if (!userData.kyc?.verified) {
        toast.error('Debes verificar tu identidad antes de retirar')
        set({ loading: false })
        return { success: false, error: 'KYC not verified' }
      }

      // Deduct amount immediately from user's wallet
      const newWinnings = balance.winnings - amount
      const newTotal = balance.total - amount
      
      await updateDoc(doc(db, 'users', user.uid), {
        'wallet.winnings': newWinnings,
        'wallet.total': newTotal,
      })

      // Create withdrawal request
      await addDoc(collection(db, 'withdrawalRequests'), {
        userId: user.uid,
        userEmail: userData.email,
        userName: userData.fullName || userData.username,
        amount,
        clabe: clabeDestination,
        beneficiaryName,
        status: 'pending',
        createdAt: serverTimestamp(),
      })

      // Add transaction record
      await addDoc(collection(db, 'transactions'), {
        userId: user.uid,
        type: 'withdrawal',
        amount,
        status: 'pending',
        description: 'Retiro por transferencia CLABE',
        createdAt: serverTimestamp(),
      })

      // Refresh balance
      await get().fetchBalance()

      toast.success('Solicitud de retiro enviada. En proceso de validación.')
      set({ loading: false })
      return { success: true }
    } catch (error) {
      console.error('Error requesting withdrawal:', error)
      toast.error('Error al solicitar retiro')
      set({ error: error.message, loading: false })
      return { success: false, error: error.message }
    }
  },

  // Get withdrawable amount
  getWithdrawableAmount: () => {
    const { balance } = get()
    return balance?.winnings || 0
  },

  // Get total balance
  getTotalBalance: () => {
    const { balance } = get()
    return balance?.total || 0
  },

  // Create Mercado Pago deposit
  createDeposit: async (amount) => {
    const user = useAuthStore.getState().user
    if (!user) {
      toast.error('Debes iniciar sesión')
      return { success: false, error: 'Not authenticated' }
    }

    try {
      set({ loading: true })

      // Call Cloud Function to create payment preference
      const createDepositPreference = httpsCallable(functions, 'createDepositPreference')
      const result = await createDepositPreference({ 
        amount: Number(amount),
        userId: user.uid 
      })

      set({ loading: false })
      
      return { 
        success: true, 
        preferenceId: result.data.preferenceId,
        initPoint: result.data.initPoint 
      }
    } catch (error) {
      console.error('Error creating deposit:', error)
      toast.error('Error al crear el pago')
      set({ error: error.message, loading: false })
      return { success: false, error: error.message }
    }
  },

  // Deduct amount from wallet (for quiniela entry)
  deductFromWallet: async (amount, description, relatedQuiniela = null) => {
    const user = useAuthStore.getState().user
    if (!user) {
      return { success: false, error: 'No user authenticated' }
    }

    try {
      set({ loading: true })

      // Get current balance
      const userDocRef = doc(db, 'users', user.uid)
      const userDoc = await getDoc(userDocRef)
      
      if (!userDoc.exists()) {
        throw new Error('Usuario no encontrado')
      }

      const currentWallet = userDoc.data().wallet || { deposited: 0, winnings: 0, total: 0 }
      
      // Check if user has enough balance
      if (currentWallet.total < amount) {
        toast.error(`Saldo insuficiente. Necesitas $${amount.toFixed(2)} pero solo tienes $${currentWallet.total.toFixed(2)}`)
        set({ loading: false })
        return { success: false, error: 'Saldo insuficiente' }
      }

      // Calculate new balance (deduct from deposited first, then winnings)
      let newDeposited = currentWallet.deposited
      let newWinnings = currentWallet.winnings
      let remaining = amount

      if (newDeposited >= remaining) {
        newDeposited -= remaining
        remaining = 0
      } else {
        remaining -= newDeposited
        newDeposited = 0
        newWinnings -= remaining
      }

      const newWallet = {
        deposited: Math.max(0, newDeposited),
        winnings: Math.max(0, newWinnings),
        total: Math.max(0, newDeposited + newWinnings)
      }

      // Update wallet
      await updateDoc(userDocRef, { wallet: newWallet })

      // Create transaction record
      await addDoc(collection(db, 'transactions'), {
        userId: user.uid,
        type: 'bet',
        amount: -amount,
        balanceBefore: currentWallet.total,
        balanceAfter: newWallet.total,
        description,
        status: 'completed',
        relatedQuiniela,
        createdAt: serverTimestamp()
      })

      // Update local state
      set({ balance: newWallet, loading: false })
      
      return { success: true, newBalance: newWallet }
    } catch (error) {
      console.error('Error deducting from wallet:', error)
      toast.error('Error al procesar el pago')
      set({ error: error.message, loading: false })
      return { success: false, error: error.message }
    }
  },
}))
